// style-substrate.ts — the ONE seam where a style's RUNTIME attaches to the
// Bridge home. The console twin of helm-bridge/src/client/style-runtime.ts.
//
// OWNER CORRECTION (2026-09-07, binding): the style axis applies with the same
// rigor on the Bridge home as on the Helm — fonts, icons, palette AND "the
// material substrate (backgrounds/canvas where the style has one, honoring
// reduce-effects)".
//
// MEASURED, before this file existed: the generated material tables carried
// the `#style-bg` RULES (3 of them, plus both reduce guards) and the console
// had ZERO references to `applyStyleBackground` — nothing ever inserted the
// ELEMENT those rules select. Five styles declare a `canvas` ground and one a
// `texture` ground; every one of them fell back to its CSS-only still on the
// home while the Helm in the next tab animated. Rules with no element is the
// same "reports success while doing nothing" half-state the styles programme
// exists to kill, one layer down from #490's.
//
// WHY THIS FILE IS HAND-WRITTEN while everything it calls is a generated
// copy: the Helm's style-runtime.ts also wires seams that are that client's
// own (its brand mark, its favicon swap, its icon injection point). Copying it
// would drag those across. The part that must NOT fork is the PAINTER TABLE —
// which style gets which canvas — and ensure-style-runtime.mjs derives that
// from the registry and REFUSES a canvas-ground style with no painter, so a
// new style cannot land on the Helm and silently render flat here.
//
// The split mirrors the Helm's exactly, and for the same reason:
//   bootStyleSubstrate() runs ONCE, BEFORE the first ground — a canvas style
//                        whose painter is not yet registered degrades to
//                        `gradient` with only a console.warn to say so.
//   groundStyle(id)      runs on EVERY repaint, from appearance.apply(), so
//                        the ground follows a style switch through the one
//                        apply path the attribute does.

import { GLASS, STARSHIP, SYNTHWAVE, QUARTERDECK, DESTROYER, resolveStyle } from './generated/styles.js';
import {
  applyStyleBackground,
  backgroundCanvasCount,
  effectiveGrade,
  GROUND_HORIZON_SEAT,
  registerCanvasPainter,
  type CanvasPainter,
} from './generated/style-axis.js';
import { paintGlassBokeh } from './generated/glass-bokeh.js';
import { paintSynthwaveGrid } from './generated/synthwave-grid.js';
import { paintQuarterdeckChart } from './generated/qd-chart.js';
import { paintStarfield, syncStarfieldGround } from './generated/starship-stars.js';
import { paintDestroyerSweep } from './generated/destroyer-sweep.js';
import { installSpecularTracker, setSpecularArmed } from './generated/glass-hover.js';
import { clearTargetLock, installTargetLock } from './generated/target-lock.js';
import { bootEffectsSeat } from './generated/effects-seat.js';
import { refreshBrand } from './generated/brand.js';

let booted = false;

/**
 * Register every style's runtime pieces and resolve the reduce-effects seat.
 *
 * Returns the line the caller LOGS. An unnamed runtime seam is how a style
 * ships with its canvas silently unregistered — the same reason the Helm's
 * bootStyleRuntime returns a line instead of void.
 */
export function bootStyleSubstrate(root: HTMLElement | null): string {
  if (booted) return 'style substrate: already booted';
  booted = true;
  const painters: Array<[string, CanvasPainter]> = [
    [GLASS.id, paintGlassBokeh],
    [SYNTHWAVE.id, paintSynthwaveGrid],
    [QUARTERDECK.id, paintQuarterdeckChart],
    [STARSHIP.id, paintStarfield],
    [DESTROYER.id, paintDestroyerSweep],
  ];
  for (const [id, paint] of painters) registerCanvasPainter(id, paint);
  installSpecularTracker(root);
  // The travelling target lock (owner 2026-09-08) — the same generated copy
  // of target-lock.ts the Helm runs, armed per hover by the style's own
  // `--btn-deco-travel` token (carried here by ensure-style-materials.mjs).
  const lock = installTargetLock(root);
  const eff = bootEffectsSeat();
  return (
    `style substrate: ${painters.length} canvas painters registered (${painters.map(([id]) => id).join(', ')}) · ` +
    `specular tracker ${root ? 'installed' : 'NOT installed (no root)'} · ${lock} · ` +
    `reduce-effects ${eff.reduced ? 'ON' : 'off'} (seat ${eff.seat} · motion ${eff.motion} · transparency ${eff.transparency})`
  );
}

/**
 * Follow a style repaint: ground the document and arm the per-style seams.
 *
 * NOT `applyAppStyle()` — that is the Helm's whole apply path and would also
 * stamp `data-style` and mirror the pick into the Helm's OWN pre-paint key
 * (`bridge-style`). The console stamps in appearance.apply() and mirrors into
 * `hc-style`; calling both would write two keys and make "which one does the
 * pre-paint replay?" ambiguous. So this takes only the GROUND.
 *
 * Returns the grade actually applied (which may be a degraded one), so the
 * caller can name it — `canvas -> gradient` is a real outcome under
 * prefers-reduced-motion and must be readable, not inferred.
 */
export function groundStyle(styleId: string): string {
  const style = resolveStyle(styleId);
  const grade = applyStyleBackground(style);
  setSpecularArmed(style.id === GLASS.id);
  clearTargetLock();
  // STARSHIP's light slot mounts no field at all — not a paused one. The one
  // mode-dependent ground in the programme converges here, through the
  // substrate's own teardown/apply, and returns early when nothing changes.
  syncStarfieldGround(style.id);
  // The tab icon (<link id="favicon">, written by the ONE writer in brand.ts)
  // and the header's logo + wordmark hooks follow the style through this same
  // path (owner ruling 2026-09-08). Before this line the console swapped its
  // icon SPRITE per style while its tab mark stayed Blueprint's — measured
  // in the asset gate's 'swap-host' seat as 1/2 hosts.
  refreshBrand();
  return grade;
}

/** One measured line for the boot log: what the ground actually resolved to. */
export function describeGround(styleId: string): string {
  const style = resolveStyle(styleId);
  const want = style.background;
  const got = effectiveGrade(want);
  // The surface's horizon seat, as written on <html> by the shell (shell.ts
  // seatGroundHorizon) — `(unset)` before a shell mounts, which is the state
  // this line is logged in at boot; the seat is read live by the painter.
  const seat = typeof document !== 'undefined'
    ? document.documentElement.style.getPropertyValue(GROUND_HORIZON_SEAT).trim() || '(unset)'
    : '(no document)';
  return (
    `style ground: style=${style.id} · ground ${want}${got === want ? '' : ` -> ${got}`}` +
    ` · canvases ${backgroundCanvasCount()}/1 · horizon seat ${GROUND_HORIZON_SEAT}=${seat}`
  );
}

/** Test seam. */
export function resetStyleSubstrate(): void {
  booted = false;
}
